import { DiscordSDK, patchUrlMappings } from '@discord/embedded-app-sdk';

const clientId = import.meta.env.VITE_CLIENT_ID;

// Detect if we're running inside Discord's Activity iframe
const queryParams = new URLSearchParams(window.location.search);
export const isEmbedded = queryParams.has('frame_id') || queryParams.has('instance_id');

export const discordSdk = isEmbedded ? new DiscordSDK(clientId) : null;

// Patch URL mappings so external resources go through Discord's proxy
// This is REQUIRED for CSP compliance inside the Activity iframe
if (discordSdk) {
  patchUrlMappings([
    // Map tile server through the proxy
    { prefix: '/tiles', target: 'a.basemaps.cartocdn.com' },
    { prefix: '/tiles-b', target: 'b.basemaps.cartocdn.com' },
    { prefix: '/tiles-c', target: 'c.basemaps.cartocdn.com' },
    { prefix: '/tiles-d', target: 'd.basemaps.cartocdn.com' },
    // Map the main site for images
    { prefix: '/site', target: 'epsteinexposed.com' },
    // Map Discord CDN for avatars
    { prefix: '/discord-cdn', target: 'cdn.discordapp.com' },
    // Google Fonts
    { prefix: '/fonts', target: 'fonts.googleapis.com' },
    { prefix: '/fontstatic', target: 'fonts.gstatic.com' },
  ]);
}

export interface DiscordUser {
  id: string;
  username: string;
  discriminator: string;
  avatar: string | null;
  global_name: string | null;
}

export interface DiscordAuth {
  accessToken: string;
  user: DiscordUser;
  guildId: string | null;
  channelId: string | null;
}

let cachedAuth: DiscordAuth | null = null;

// Mock auth for standalone browser access (no Discord)
const STANDALONE_AUTH: DiscordAuth = {
  accessToken: 'standalone',
  user: {
    id: '0',
    username: 'Viewer',
    discriminator: '0000',
    avatar: null,
    global_name: 'Evidence Viewer',
  },
  guildId: null,
  channelId: null,
};

export async function setupDiscord(): Promise<DiscordAuth> {
  if (cachedAuth) return cachedAuth;

  // Standalone mode — skip Discord auth, go straight to the app
  if (!discordSdk) {
    cachedAuth = STANDALONE_AUTH;
    return cachedAuth;
  }

  // --- Inside Discord Activity iframe ---

  // Wait for SDK to be ready
  await discordSdk.ready();

  // Authorize with Discord
  const { code } = await discordSdk.commands.authorize({
    client_id: clientId,
    response_type: 'code',
    state: '',
    prompt: 'none',
    scope: ['identify', 'guilds'],
  });

  // Exchange code for token via our server
  const tokenRes = await fetch('/api/discord/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!tokenRes.ok) {
    const errBody = await tokenRes.text();
    throw new Error(`Token exchange failed: ${tokenRes.status} ${errBody}`);
  }

  const { access_token } = await tokenRes.json();

  // Authenticate with the SDK
  const authResult = await discordSdk.commands.authenticate({
    access_token,
  });

  if (!authResult) {
    throw new Error('Authentication failed');
  }

  // Fetch user info — use proxied Discord API
  const userRes = await fetch('https://discord.com/api/v10/users/@me', {
    headers: { Authorization: `Bearer ${access_token}` },
  });
  const user: DiscordUser = await userRes.json();

  cachedAuth = {
    accessToken: access_token,
    user,
    guildId: discordSdk.guildId ?? null,
    channelId: discordSdk.channelId ?? null,
  };

  return cachedAuth;
}

export function getAuth(): DiscordAuth | null {
  return cachedAuth;
}

export function getAvatarUrl(user: DiscordUser, size = 64): string {
  if (user.avatar) {
    return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.webp?size=${size}`;
  }
  const idx = user.id === '0' ? 0 : Number(BigInt(user.id) >> 22n) % 6;
  return `https://cdn.discordapp.com/embed/avatars/${idx}.png`;
}

export function getInstanceId(): string {
  return discordSdk?.instanceId ?? `standalone-${Date.now()}`;
}
